import { useDatasets } from 'api/dataset/dataset.api'
import { useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import { getReturnIdsFromDatasetsInstruction } from 'helpers/dataset.helper/dataset.helper'
import {
  filterDatasetInstructionsNotShown,
  getDigitalTwinTagsFromDataId,
  getItemsFromVariableCalcs,
  getItemsWithTags,
} from 'helpers/filterComponent.helper/filterComponent.helper'
import { isNumber, useHasPermission } from 'helpers/global.helper/global.helper'
import { useGrouping } from 'helpers/grouping.helper/grouping.helper'
import { getRoute } from 'helpers/route.helper/route.helper'
import { hideRestrictiveUiConfigItems } from 'helpers/uiConfig.helper/uiConfig.helper'
import React, { memo, ReactElement, useCallback, useMemo, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useNavigate } from 'react-router'
import elplanStore from 'store/elplan/elplan'
import filterStore, { getGrouping, NO_GROUPING, setGrouping } from 'store/filter/filter'
import uiConfigStore from 'store/uiConfig/uiConfig'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ErrorFallback from 'ui/components/ErrorFallback/ErrorFallback'
import UiConfigDebugMenu from 'ui/components/UiConfigDebugMenu/UiConfigDebugMenu'
import getUiConfigComponent, { getComponentHeight } from 'ui/uiConfig/factory'
import sentry from 'utils/sentry/sentry'
import { snapshot, useSnapshot } from 'valtio'
import { parseWildcardItems } from './components/wildcardItems.helper'
import styles from './UiConfigComponent.module.less'

type UiConfigComponentProps = {
  id?: number
  uid?: number
  systemId?: number | null
  datasetStartTime?: ISODateTime
  datasetEndTime?: ISODateTime
  noWrappingDiv?: boolean
  tourId?: string
  ignoreZoom?: boolean
  overrideAlias?: UiConfigAliases
  overrideProps?: UiConfigOverrideProps
  datasetRefreshToken?: string
  key_prefix?: string | number
  onDatasets?: (uid: number, datasets: Dataset[]) => void
  datasetCreatedAt?: string
  datasetCreatedAtOperator?: DatasetCreatedAtOperators
}

function UiConfigComponent({
  id: uiConfigId,
  uid,
  systemId,
  datasetStartTime,
  datasetEndTime,
  noWrappingDiv,
  tourId,
  ignoreZoom: _ignoreZoom,
  overrideAlias,
  overrideProps = {},
  datasetRefreshToken,
  key_prefix = '',
  onDatasets,
  datasetCreatedAt,
  datasetCreatedAtOperator,
}: UiConfigComponentProps): ReactElement | null {
  const uiConfigSnap = useSnapshot(uiConfigStore)
  const { allTags, activeTagsForUiConfig: activeTags } = useSnapshot(filterStore)
  const navigate = useNavigate()
  const { elplan_info_id: elplanInfoId, bid_types: bidTypes } = useSnapshot(elplanStore)
  const route = getRoute()

  if (!uiConfigId && uid) {
    uiConfigId = uiConfigSnap.uiConfigs[uid]?.id
  }
  if (!uid && uiConfigId) {
    uid = uiConfigSnap.idToUiConfig[uiConfigId]?.uid as number
  }
  if (!systemId && uiConfigId) {
    systemId = uiConfigSnap.idToUiConfig[uiConfigId]?.system
  }

  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)
  const uiConfig = uid
    ? uiConfigSnap.getParsedUiConfig(uid, overrideAlias, {
        overrideProps,
      })
    : null
  const allTagsDict = useMemo(() => digitalTwin?.model?.all_tags_dict || {}, [digitalTwin?.model])
  const uiConfigProps = { ...uiConfig?.props }

  const applyFilterComponent = uiConfigProps.include_items_in_filtering && !uiConfigProps.ignore_filter_component

  if (uiConfigProps.has_filter_component) {
    filterStore.filterComponentPerRoute[route] = true
  }

  // Hide restrictive items
  const hideRestrictiveItems = useHasPermission('hide_restrictive_ui_config_items')
  if (hideRestrictiveItems) {
    uiConfigProps.items = uiConfigProps.items?.filter(
      (item) => !hideRestrictiveUiConfigItems(item, hideRestrictiveItems)
    )
  }

  // Using useRef instead of useEffect as the conditions for applying the filter component are not static and in risk of rerendering too many times with a dependency array
  const initializeFiltering = useRef(applyFilterComponent ?? false)
  if (initializeFiltering.current && uiConfig?.props?.items && digitalTwin?.model?.nodes) {
    const tagsPerItem: string[][] = []
    uiConfig?.props?.items?.forEach((item) => {
      const dataIdComponents = item?.data_id?.split('.')
      const unitName = dataIdComponents[dataIdComponents.length - 1]
      const digitalTwinTags = getDigitalTwinTagsFromDataId(unitName, allTagsDict) ?? []

      tagsPerItem.push(item.tags ?? digitalTwinTags)
    })
    filterStore.tagsPerUiconfig = [...filterStore.tagsPerUiconfig, ...tagsPerItem]
    if (uiConfigProps.include_items_in_grouping && !getGrouping()) {
      setGrouping(NO_GROUPING, navigate)
    }
    initializeFiltering.current = false
  }

  const itemsWithTags = useMemo(() => {
    if (applyFilterComponent) {
      const itemsMergedWithTags = getItemsWithTags(uiConfigProps.items ?? [], allTagsDict)
      const itemsFromVariableCalcs = getItemsFromVariableCalcs(
        uiConfig?.dataset_instructions ?? [],
        itemsMergedWithTags,
        allTagsDict
      ) //creates items of variables in calcs to be used in filtering of dataset_instructions
      return { itemWithTags: itemsMergedWithTags, fakeItemsFromVariableCalcs: itemsFromVariableCalcs }
    }

    return { itemWithTags: uiConfigProps.items ?? [], fakeItemsFromVariableCalcs: [] }
  }, [allTagsDict, applyFilterComponent, uiConfig?.dataset_instructions, uiConfigProps.items])

  const itemsWithWildCards: UiConfigPropsItem[] | undefined = useMemo(() => {
    const hasWildcardBidItems = uiConfig?.props.items?.some(
      (item) => item?.data_id?.includes('*') && bidTypes.some((bt) => item.data_id.includes(bt.type))
    )
    if (hasWildcardBidItems && elplanInfoId !== 0) {
      return parseWildcardItems(uiConfig?.props.items as UiConfigPropsItem[])
    }
    return undefined
  }, [elplanInfoId, bidTypes, uiConfig?.props.items])

  const { filteredDatasetInstructions, onlyReturnIds } = useMemo(() => {
    let filteredDatasetInstructions: Partial<DatasetInstruction>[]
    if (applyFilterComponent) {
      //only filter DSIs if any tags exists
      if (allTags.size > 0) {
        filteredDatasetInstructions = filterDatasetInstructionsNotShown(
          uiConfig?.dataset_instructions ?? [],
          itemsWithTags.itemWithTags.concat(itemsWithTags.fakeItemsFromVariableCalcs),
          activeTags.size > 0 ? activeTags : allTags, //if activeTags has not been set yet, take allTags as a fallback
          allTags,
          allTagsDict
        )
      } else {
        filteredDatasetInstructions = []
      }
    } else {
      filteredDatasetInstructions = uiConfig?.dataset_instructions ?? []
    }
    const onlyReturnIds = getReturnIdsFromDatasetsInstruction(filteredDatasetInstructions)

    return {
      filteredDatasetInstructions,
      onlyReturnIds,
    }
  }, [
    applyFilterComponent,
    activeTags,
    allTags,
    uiConfig?.dataset_instructions,
    itemsWithTags.itemWithTags,
    itemsWithTags.fakeItemsFromVariableCalcs,
    allTagsDict,
  ])

  const ignoreZoom = _ignoreZoom || !!uiConfig?.props?.ignore_zoom
  const datasetRes = useDatasets(
    uiConfig?.id || 0,
    uiConfig?.version || 0,
    filteredDatasetInstructions ?? [],
    datasetStartTime,
    datasetEndTime,
    {
      uid,
      datasetRefreshToken,
      ignoreZoom,
      overrideAlias,
      onlyReturnIds,
      datasetCreatedAt,
      datasetCreatedAtOperator,
      isFiltered: applyFilterComponent,
    }
  )
  let datasets = datasetRes.data || []
  //group datasets if needed
  const { datasets: groupedDatasets, items: groupedItems } = useGrouping(
    itemsWithTags.itemWithTags,
    uiConfigProps,
    datasets,
    uid,
    digitalTwin?.model?.hidden_default_tags ?? [],
    getGrouping(),
    uiConfig?.props?.grouping_tag,
    uiConfig?.props?.tags_config
  )

  if (groupedDatasets && groupedItems) {
    datasets = groupedDatasets
    uiConfigProps.items = groupedItems
  }

  const showPlaceholder = !datasets?.length && uiConfig?.dataset_instructions?.length && datasetRes.isFetching

  const translations = digitalTwin?.translations

  // Memoize the onDatasets callback to prevent children re-renders
  const handleChildDatasets = useCallback(
    (childUid: number, childDatasets: Dataset[]) => {
      if (onDatasets) {
        onDatasets(childUid, childDatasets)
      }
    },
    [onDatasets]
  )

  // Memoize children IDs lookup to avoid recalculating on every render
  const childrenIdPairs = useMemo(() => {
    const uiConfigsSnap = snapshot(uiConfigStore)
    return (uiConfig?.children_ids || [])
      .map((id) => {
        for (const key in uiConfigsSnap.uiConfigs) {
          const config = uiConfigsSnap.uiConfigs[key]
          if (config.id === id) {
            return [id, config.uid] as [number, number]
          }
        }
        return [undefined, undefined] as [undefined, undefined]
      })
      .filter((pair): pair is [number, number] => isNumber(pair[0]) && isNumber(pair[1]))
  }, [uiConfig?.children_ids])

  // Memoize children components to prevent unnecessary re-renders
  const children = useMemo(
    () =>
      childrenIdPairs.map(([id, childUid]) => (
        <UiConfigComponent
          uid={childUid}
          key={`${key_prefix}${childUid}`}
          id={id}
          datasetStartTime={datasetStartTime}
          datasetEndTime={datasetEndTime}
          datasetCreatedAt={datasetCreatedAt}
          datasetCreatedAtOperator={datasetCreatedAtOperator}
          ignoreZoom={ignoreZoom}
          overrideAlias={overrideAlias}
          datasetRefreshToken={datasetRefreshToken}
          onDatasets={handleChildDatasets}
        />
      )),
    [
      childrenIdPairs,
      key_prefix,
      datasetStartTime,
      datasetEndTime,
      datasetCreatedAt,
      datasetCreatedAtOperator,
      ignoreZoom,
      overrideAlias,
      datasetRefreshToken,
      handleChildDatasets,
    ]
  )

  // Memoize debug menu to prevent re-renders
  const debugMenu = useMemo(
    () => (
      <UiConfigDebugMenu
        uid={uid}
        id={uiConfigId || uiConfig?.id}
        datasets={datasets}
        datasetStartTime={datasetStartTime}
        datasetEndTime={datasetEndTime}
        datasetCreatedAt={datasetCreatedAt}
        datasetCreatedAtOperator={datasetCreatedAtOperator}
        overrideAlias={overrideAlias}
      />
    ),
    [
      uid,
      uiConfigId,
      uiConfig?.id,
      datasets,
      datasetStartTime,
      datasetEndTime,
      datasetCreatedAt,
      datasetCreatedAtOperator,
      overrideAlias,
    ]
  )

  if (!uiConfig) {
    return <></>
  }
  const Component = getUiConfigComponent(uiConfig.component)
  if (!Component) {
    return null
  }

  if (uid && onDatasets) {
    onDatasets(uid, datasets)
  }

  if (noWrappingDiv) {
    if (showPlaceholder) {
      return (
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'UiConfigComponentPlaceholder' })}
        >
          <LoadingPlaceholderContainer height={getComponentHeight(uiConfig.component)} />
        </ErrorBoundary>
      )
    }

    return (
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'UiConfigComponentNoWrapper' })}
      >
        {/* `contents` generates no layout box, so this keeps the widget identifiable without
            reintroducing the wrapper that `noWrappingDiv` exists to suppress. */}
        <div className="contents" data-component={uiConfig.component}>
          {/* e2e: identifies which widget rendered, same contract as the wrapped path — do not remove */}
          <Component
            component={uiConfig.component}
            key={`${key_prefix}${uid}`}
            uid={uid}
            legendKey={`uiconfig_${uiConfig.id}`}
            systemId={systemId}
            {...uiConfigProps}
            items={itemsWithWildCards ?? uiConfigProps.items}
            ignoreZoom={ignoreZoom}
            datasets={datasets}
            datasetStartTime={datasetStartTime}
            datasetEndTime={datasetEndTime}
            datasetCreatedAt={datasetCreatedAt}
            datasetCreatedAtOperator={datasetCreatedAtOperator}
            overrideAlias={overrideAlias}
            datasetRefreshToken={datasetRefreshToken}
            translations={translations}
          >
            {children}
          </Component>
        </div>
        {debugMenu}
      </ErrorBoundary>
    )
  }
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'UiConfigComponentWrapped' })}
    >
      <div
        className={styles.UiConfigComponent + (tourId !== undefined ? ` ` + tourId : ``)}
        data-id={uiConfig.id}
        data-version={uiConfig.version}
        data-uid={uiConfig.uid}
        data-component={uiConfig.component} // e2e: identifies which widget rendered (stable component name, unlike uid/version) — do not remove
      >
        {uiConfig.mode === `beta` && <div className={styles.UiConfigComponent_Beta}>Beta</div>}
        {showPlaceholder ? (
          <LoadingPlaceholderContainer height={getComponentHeight(uiConfig.component)} />
        ) : (
          <>
            <Component
              component={uiConfig.component}
              key={`${key_prefix}${uid}`}
              uid={uid}
              legendKey={`uiconfig_${uiConfig.id}`}
              systemId={systemId}
              ignoreZoom={ignoreZoom}
              {...uiConfigProps}
              items={itemsWithWildCards ?? uiConfigProps.items}
              datasets={datasets}
              datasetStartTime={datasetStartTime}
              datasetEndTime={datasetEndTime}
              datasetCreatedAt={datasetCreatedAt}
              datasetCreatedAtOperator={datasetCreatedAtOperator}
              overrideAlias={overrideAlias}
              datasetRefreshToken={datasetRefreshToken}
              translations={translations}
            >
              {children}
            </Component>
          </>
        )}
        {debugMenu}
      </div>
    </ErrorBoundary>
  )
}

export default memo(UiConfigComponent)
